import React, { useState, useCallback, useEffect } from 'react';
import * as importApi from '../services/importApi.ts';
import './ImportRules.css';

interface Account { id: number; name: string; }
interface Category { id: number; name: string; type: string; }

const emptyForm = (): importApi.CreateRuleRequest => ({
  name: '',
  pattern: '',
  action_type: 'rendimento_fii',
  account_id: null,
  category_id: null,
  priority: 0,
});

const ImportRules: React.FC = () => {
  const [rules, setRules] = useState<importApi.ImportMappingRule[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<importApi.ImportMappingRule | null>(null);
  const [form, setForm] = useState<importApi.CreateRuleRequest>(emptyForm());
  const [saving, setSaving] = useState(false);
  // Preview
  const [previewInput, setPreviewInput] = useState('');
  const [previewResults, setPreviewResults] = useState<importApi.PreviewResult[]>([]);
  const [previewing, setPreviewing] = useState(false);
  // Backfill
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<importApi.BackfillResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesData, accountsData, categoriesData] = await Promise.all([
        importApi.getRules(),
        getAccounts(),
        getCategories(),
      ]);
      setRules(rulesData);
      setAccounts(accountsData);
      setCategories(categoriesData);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        account_id: showsAccountCategory(form.action_type) ? form.account_id : null,
        category_id: showsAccountCategory(form.action_type) ? form.category_id : null,
        priority: editingRule ? editingRule.priority : rules.length,
      };
      if (editingRule) {
        await importApi.updateRule(editingRule.id, payload);
      } else {
        await importApi.createRule(payload);
      }
      setShowForm(false);
      setEditingRule(null);
      setForm(emptyForm());
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao salvar regra');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rule: importApi.ImportMappingRule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      pattern: rule.pattern,
      action_type: rule.action_type,
      account_id: rule.account_id,
      category_id: rule.category_id,
      priority: rule.priority,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Excluir esta regra?')) return;
    try {
      await importApi.deleteRule(id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao excluir regra');
    }
  };

  const moveRule = async (idx: number, direction: -1 | 1) => {
    const newRules = [...rules];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= newRules.length) return;
    [newRules[idx], newRules[swapIdx]] = [newRules[swapIdx], newRules[idx]];
    setRules(newRules);
    try {
      await importApi.reorderRules(newRules.map(r => r.id));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao reordenar regras');
      await load();
    }
  };

  const handlePreview = async () => {
    const descriptions = previewInput
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
    if (descriptions.length === 0) return;
    setPreviewing(true);
    try {
      const results = await importApi.previewRules(descriptions);
      setPreviewResults(results);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao testar regras');
    } finally {
      setPreviewing(false);
    }
  };

  const handleBackfill = async () => {
    if (!window.confirm('Isso criará receitas retroativas para todos os rendimentos FII importados anteriormente que possuem conta e categoria configuradas na regra. Continuar?')) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const result = await importApi.backfillFIITransactions();
      setBackfillResult(result);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao executar backfill');
    } finally {
      setBackfilling(false);
    }
  };

  const needsAccountCategory = (actionType: importApi.ImportActionType) =>
    actionType === 'receita' || actionType === 'despesa';

  const showsAccountCategory = (actionType: importApi.ImportActionType) =>
    actionType === 'receita' || actionType === 'despesa' || actionType === 'rendimento_fii';

  const filteredCategories = categories.filter(c => {
    if (form.action_type === 'receita') return c.type === 'income';
    if (form.action_type === 'despesa') return c.type === 'expense';
    return true;
  });

  if (loading) return <div className="import-rules-loading">Carregando...</div>;

  return (
    <div className="import-rules">
      <div className="import-rules-header">
        <h2>Regras de Importação</h2>
        <button
          className="btn-primary"
          onClick={() => {
            setEditingRule(null);
            setForm(emptyForm());
            setShowForm(true);
          }}
        >
          + Nova Regra
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="import-rules-form" onSubmit={handleSubmit}>
          <h3>{editingRule ? 'Editar Regra' : 'Nova Regra'}</h3>

          <div className="form-section-label">Critério de correspondência</div>
          <div className="form-row">
            <div className="form-group">
              <label>Nome da regra</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Rendimentos FII"
                required
              />
            </div>
            <div className="form-group">
              <label>
                Padrão{' '}
                <span className="form-hint">Use * como curinga</span>
              </label>
              <input
                type="text"
                value={form.pattern}
                onChange={e => setForm(f => ({ ...f, pattern: e.target.value }))}
                placeholder="Ex: RENDIMENTOS DE CLIENTES * S/*"
                required
              />
            </div>
          </div>

          <div className="form-section-label">Ação a realizar</div>
          <div className="form-group">
            <label>Tipo de ação</label>
            <div className="action-type-options">
              {(
                [
                  { value: 'rendimento_fii', label: 'Rendimento FII', desc: 'Registra rendimento para um FII da carteira' },
                  { value: 'receita',        label: 'Crédito em conta', desc: 'Cria uma receita paga na conta selecionada' },
                  { value: 'despesa',        label: 'Débito em conta',  desc: 'Cria uma despesa paga na conta selecionada' },
                  { value: 'ignorar',        label: 'Ignorar',          desc: 'Descarta o lançamento sem criar nenhum registro' },
                ] as { value: importApi.ImportActionType; label: string; desc: string }[]
              ).map(opt => (
                <label
                  key={opt.value}
                  className={`action-option ${form.action_type === opt.value ? 'selected' : ''}`}
                  style={form.action_type === opt.value ? { borderColor: importApi.ACTION_COLORS[opt.value], background: importApi.ACTION_COLORS[opt.value] + '18' } : {}}
                >
                  <input
                    type="radio"
                    name="action_type"
                    value={opt.value}
                    checked={form.action_type === opt.value}
                    onChange={() => setForm(f => ({ ...f, action_type: opt.value, account_id: null, category_id: null }))}
                  />
                  <span className="action-option-label">{opt.label}</span>
                  <span className="action-option-desc">{opt.desc}</span>
                </label>
              ))}
            </div>
          </div>

          {showsAccountCategory(form.action_type) && (
            <div className="form-row">
              <div className="form-group">
                <label>
                  Conta
                  {form.action_type === 'rendimento_fii' && <span className="label-optional"> (opcional — gera crédito em conta)</span>}
                </label>
                <select
                  value={form.account_id ?? ''}
                  onChange={e => setForm(f => ({ ...f, account_id: e.target.value ? Number(e.target.value) : null }))}
                  required={needsAccountCategory(form.action_type)}
                >
                  <option value="">Selecione a conta</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Categoria</label>
                <select
                  value={form.category_id ?? ''}
                  onChange={e => setForm(f => ({ ...f, category_id: e.target.value ? Number(e.target.value) : null }))}
                  required={needsAccountCategory(form.action_type)}
                >
                  <option value="">Selecione a categoria</option>
                  {filteredCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : editingRule ? 'Atualizar' : 'Criar'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setShowForm(false); setEditingRule(null); }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Rules List */}
      {rules.length === 0 ? (
        <div className="import-rules-empty">
          <p>Nenhuma regra cadastrada.</p>
          <p>Crie regras para mapear automaticamente os lançamentos do extrato.</p>
        </div>
      ) : (
        <div className="rules-list">
          <p className="rules-hint">Regras são aplicadas em ordem — a primeira que casar vence.</p>
          {rules.map((rule, idx) => (
            <div key={rule.id} className="rule-card">
              <div className="rule-order">
                <button
                  className="order-btn"
                  onClick={() => moveRule(idx, -1)}
                  disabled={idx === 0}
                  title="Mover para cima"
                >▲</button>
                <span>{idx + 1}</span>
                <button
                  className="order-btn"
                  onClick={() => moveRule(idx, 1)}
                  disabled={idx === rules.length - 1}
                  title="Mover para baixo"
                >▼</button>
              </div>
              <div className="rule-info">
                <div className="rule-name">{rule.name}</div>
                <div className="rule-pattern">
                  <code>{rule.pattern}</code>
                </div>
              </div>
              <div className="rule-meta">
                <span
                  className="rule-badge"
                  style={{ backgroundColor: importApi.ACTION_COLORS[rule.action_type] }}
                >
                  {importApi.ACTION_LABELS[rule.action_type]}
                </span>
                {(needsAccountCategory(rule.action_type) || (rule.action_type === 'rendimento_fii' && rule.account_id)) && (
                  <span className="rule-destination">
                    {rule.account_id ? (accounts.find(a => a.id === rule.account_id)?.name ?? `conta #${rule.account_id}`) : '—'}
                    {rule.category_id && (
                      <> · {categories.find(c => c.id === rule.category_id)?.name ?? `cat #${rule.category_id}`}</>
                    )}
                  </span>
                )}
              </div>
              <div className="rule-actions">
                <button className="btn-icon" onClick={() => handleEdit(rule)} title="Editar">✏️</button>
                <button className="btn-icon btn-danger" onClick={() => handleDelete(rule.id)} title="Excluir">🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Backfill Panel */}
      <div className="preview-panel">
        <h3>Corrigir Rendimentos FII anteriores</h3>
        <p className="form-hint">
          Cria receitas retroativas para rendimentos FII já importados que não geraram transação em conta,
          usando a conta e categoria configuradas na regra correspondente.
        </p>
        <button
          className="btn-secondary"
          onClick={handleBackfill}
          disabled={backfilling}
        >
          {backfilling ? 'Processando...' : 'Executar correção'}
        </button>
        {backfillResult && (
          <div className="preview-results" style={{ marginTop: '0.75rem' }}>
            <div className="preview-result-item matched">
              <strong>{backfillResult.created}</strong> receita(s) criada(s) &nbsp;·&nbsp;
              <strong>{backfillResult.skipped}</strong> ignorada(s) (sem regra ou já existente)
            </div>
            {backfillResult.errors.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                {backfillResult.errors.map((e, i) => (
                  <div key={i} className="preview-result-item unmatched">
                    <span className="preview-desc">{e.description}</span>
                    <span style={{ color: '#dc2626', fontSize: '0.8rem' }}>{e.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Panel */}
      <div className="preview-panel">
        <h3>Testar Regras</h3>
        <p className="form-hint">Cole descrições do extrato (uma por linha) para ver qual regra seria aplicada.</p>
        <textarea
          className="preview-input"
          rows={5}
          value={previewInput}
          onChange={e => setPreviewInput(e.target.value)}
          placeholder={`RENDIMENTOS DE CLIENTES MANA11 S/            457\nDevolução Tx de Distr Tork Long Only Institucional FIF em Cotas de FIA\nTaxa Gestao Externa s/ Carteira Adm 10800077`}
        />
        <button
          className="btn-secondary"
          onClick={handlePreview}
          disabled={previewing || !previewInput.trim()}
        >
          {previewing ? 'Testando...' : 'Testar'}
        </button>

        {previewResults.length > 0 && (
          <div className="preview-results">
            {previewResults.map((r, i) => (
              <div key={i} className={`preview-result-item ${r.matched_rule_id ? 'matched' : 'unmatched'}`}>
                <div className="preview-desc">{r.description}</div>
                {r.matched_rule_id ? (
                  <div className="preview-match">
                    <span className="rule-badge" style={{ backgroundColor: importApi.ACTION_COLORS[r.action_type!] }}>
                      {importApi.ACTION_LABELS[r.action_type!]}
                    </span>
                    <span className="preview-rule-name">{r.matched_rule_name}</span>
                    {r.captured_values?.length > 0 && (
                      <span className="preview-captured">
                        capturou: {r.captured_values.join(', ')}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="preview-no-match">Sem regra correspondente — ficará pendente</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to avoid import loop - inline simple versions
async function getAccounts(): Promise<Account[]> {
  const { api } = await import('../services/api.ts');
  const r = await api.get<Account[]>('/api/v1/accounts');
  return r.data;
}

async function getCategories(): Promise<Category[]> {
  const { api } = await import('../services/api.ts');
  const r = await api.get<Category[]>('/api/v1/categories');
  return r.data;
}

export default ImportRules;
